import React, { useState, useEffect } from "react";
import "./NewMissionForm.css";
import axios from "axios";

const NewMissionForm = () => {
  const [selectedAffaire, setSelectedAffaire] = useState("");
  const [selectedFacturation, setSelectedFacturation] = useState("");
  const [selectedDomaines, setSelectedDomaines] = useState([]);
  const [domaineDetails, setDomaineDetails] = useState([]);
  const [formIsValid, setFormIsValid] = useState(false);
  const [formIsSent, setFormIsSent] = useState(false);
  const [totalPercentageError, setTotalPercentageError] = useState("");
  const [subPercentageError, setSubPercentageError] = useState("");
  const [selectedTypeMission, setSelectedTypeMission] = useState("");
  const [offersData, setOffersData] = useState([]);
  const [missionsData, setMissionsData] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const clearAllFields = () => {
    setSelectedAffaire("");
    setSelectedFacturation("");
    setSelectedDomaines([]);
    setDomaineDetails([]);
    setFormIsValid(false);
    setTotalPercentageError("");
    setSubPercentageError("");
    setSelectedTypeMission("");
  };

  const removeMissionFromData = (affaire) => {
    setMissionsData((prevMissionsData) =>
      prevMissionsData.filter((mission) => mission.Affaire !== affaire)
    );
  };

  // Manual refresh function to clear cache and fetch fresh data
  const handleManualRefresh = async () => {
    setIsRefreshing(true);

    try {
      // Clear localStorage cache
      localStorage.removeItem("missionsData");
      localStorage.removeItem("offersData");

      const backendUrl =
        "https://us-central1-dataplaform-iac.cloudfunctions.net/readFiles";

      // Fetch fresh missions data
      const missionsResponse = await axios.get(
        backendUrl + "/new-missions-csv",
        { responseType: "text" }
      );
      const missionsJsonObj = JSON.parse(missionsResponse.data);
      setMissionsData(missionsJsonObj);
      localStorage.setItem("missionsData", JSON.stringify(missionsJsonObj));

      // Fetch fresh offers data
      const offersResponse = await axios.get(backendUrl + "/offers-json", {
        responseType: "text",
      });
      const offersJsonObj = JSON.parse(offersResponse.data);
      setOffersData(offersJsonObj);
      localStorage.setItem("offersData", JSON.stringify(offersJsonObj));

      // Clear form fields
      clearAllFields();

      // Update last refresh time
      setLastRefreshTime(new Date());

      alert(
        "Données actualisées avec succès ! Toutes les missions sont maintenant synchronisées."
      );
    } catch (error) {
      console.error("Erreur lors de l'actualisation des données:", error);
      alert("Erreur lors de l'actualisation des données. Veuillez réessayer.");
    } finally {
      setIsRefreshing(false);
    }
  };

  // Separate function for auto-refresh to avoid dependency issues
  const handleAutoRefresh = async () => {
    try {
      const backendUrl =
        "https://us-central1-dataplaform-iac.cloudfunctions.net/readFiles";

      // Fetch fresh missions data without clearing form
      const missionsResponse = await axios.get(
        backendUrl + "/new-missions-csv",
        { responseType: "text" }
      );
      const missionsJsonObj = JSON.parse(missionsResponse.data);
      setMissionsData(missionsJsonObj);
      localStorage.setItem("missionsData", JSON.stringify(missionsJsonObj));

      setLastRefreshTime(new Date());
    } catch (error) {
      console.error("Erreur lors de l'auto-actualisation des données:", error);
    }
  };

  // 1. Load missions and offers from localStorage or backend
  useEffect(() => {
    const loadData = () => {
      const localMissions = localStorage.getItem("missionsData");
      const localOffers = localStorage.getItem("offersData");

      if (localMissions) {
        setMissionsData(JSON.parse(localMissions));
      }

      if (localOffers) {
        setOffersData(JSON.parse(localOffers));
      }

      const backendUrl =
        "https://us-central1-dataplaform-iac.cloudfunctions.net/readFiles";

      // Load offers data if not in localStorage
      if (!localOffers) {
        setOffersLoading(true);
        axios
          .get(backendUrl + "/offers-json", { responseType: "text" })
          .then((response) => {
            const jsonObj = JSON.parse(response.data);
            setOffersData(jsonObj);
            setOffersLoading(false);
            // Save to localStorage for future loads
            localStorage.setItem("offersData", JSON.stringify(jsonObj));
          })
          .catch((error) => {
            console.error(
              "Erreur lors de la récupération des données de offers.csv :",
              error
            );
            setOffersLoading(false);
            // Clear localStorage to allow retry
            localStorage.removeItem("offersData");
          });
      }

      // Load missions data if not in localStorage
      if (!localMissions) {
        axios
          .get(backendUrl + "/new-missions-csv", { responseType: "text" })
          .then((response) => {
            const jsonObj = JSON.parse(response.data);
            setMissionsData(jsonObj);
            // Save to localStorage for future loads
            localStorage.setItem("missionsData", JSON.stringify(jsonObj));
          })
          .catch((error) => {
            console.error(
              "Erreur lors de la récupération des données de new_missions.csv :",
              error
            );
          });
      }
    };

    loadData();

    // Auto-refresh when user returns to the tab (visibility change)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Small delay to ensure the tab is fully active
        setTimeout(() => {
          handleAutoRefresh();
        }, 1000);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup event listener
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // 2. Sync localStorage whenever missionsData or offersData changes
  useEffect(() => {
    localStorage.setItem("missionsData", JSON.stringify(missionsData));
  }, [missionsData]);

  useEffect(() => {
    if (offersData.length > 0) {
      localStorage.setItem("offersData", JSON.stringify(offersData));
    }
  }, [offersData]);

  const getBlockOptionsForDomaine = (domaine) => {
    // Return empty array if offersData is not loaded yet
    if (!offersData || offersData.length === 0 || offersLoading) {
      return [];
    }

    // Check for different possible domain name variations
    let domainOffers = offersData.filter((offer) => {
      return offer.Domaine === domaine;
    });

    // If no exact match found, try alternative domain names
    if (domainOffers.length === 0) {
      // For C&I domain, try different variations
      if (domaine === "C&I") {
        domainOffers = offersData.filter((offer) => {
          const offerDomaine = offer.Domaine;
          return (
            offerDomaine === "C&I" ||
            offerDomaine === "CI" ||
            offerDomaine === "C_I"
          );
        });
      }
    }

    const uniqueBlocks = [...new Set(domainOffers.map((offer) => offer.Offre))];
    return uniqueBlocks;
  };

  const handleAffaireChange = (event) => {
    setSelectedAffaire(event.target.value);
  };

  // Function to extract client and domaine from mission name
  const extractClientAndDomaine = (missionName) => {
    if (!missionName) return { client: null, domaine: null };

    // Pattern: "CLIENT_NAME DOMAINE-XX-XXXX"
    // Look for domain patterns followed by dash and numbers
    const domainPatterns = ["DW", "C&I", "CI", "C_I", "EN", "CS", "INS", "TRANSV"];

    for (const domain of domainPatterns) {
      // Escape special characters in domain for regex
      const escapedDomain = domain.replace(/[&]/g, "\\&");
      const regex = new RegExp(`(.*?)\\s*${escapedDomain}-\\d+`, "i");
      const match = missionName.match(regex);
      if (match) {
        const client = match[1].trim();
        // Normalize domain name (convert CI/C_I to C&I for consistency)
        const normalizedDomain =
          domain === "CI" || domain === "C_I" ? "C&I" : domain;
        return { client, domaine: normalizedDomain };
      }
    }

    // Fallback: if no domain pattern found, return null
    return { client: null, domaine: null };
  };

  const handleFacturationChange = (event) => {
    setSelectedFacturation(event.target.value);
  };

  const handleTypeMissionChange = (event) => {
    setSelectedTypeMission(event.target.value);
  };

  const handleDomaineChange = (event) => {
    const selectedDomaine = event.target.value;
    setSelectedDomaines((prevDomaines) =>
      prevDomaines.includes(selectedDomaine)
        ? prevDomaines.filter((domaine) => domaine !== selectedDomaine)
        : [...prevDomaines, selectedDomaine]
    );
  };

  const handleDomaineDetailsChange = (domaine, type, value) => {
    const updatedDomaineDetails = [...domaineDetails];
    const index = updatedDomaineDetails.findIndex(
      (detail) => detail.domaine === domaine
    );
    if (index !== -1) {
      updatedDomaineDetails[index][type] = value;
    } else {
      updatedDomaineDetails.push({ domaine, [type]: value });
    }
    setDomaineDetails(updatedDomaineDetails);
  };

  const validateDomaineDetails = () => {
    let isValid = true;

    selectedDomaines.forEach((domaine) => {
      const domaineDetailsForDomaine = domaineDetails.filter(
        (detail) => detail.domaine === domaine
      );
      const totalPercentageOffrePrincipale = domaineDetailsForDomaine.reduce(
        (sum, detail) => sum + Number(detail.percentageOffrePrincipale || 0),
        0
      );
      const totalPercentageOffreSecondaire = domaineDetailsForDomaine.reduce(
        (sum, detail) => sum + Number(detail.percentageOffreSecondaire || 0),
        0
      );

      if (
        totalPercentageOffrePrincipale + totalPercentageOffreSecondaire !==
        100
      ) {
        setSubPercentageError(
          `La somme des pourcentages pour le domaine ${domaine} doit être égale à 100%.`
        );
        isValid = false;
      }
    });

    return isValid;
  };

  const PROD_Update_New_Mission_URL =
    "https://europe-west2-dataplaform-iac.cloudfunctions.net/PROD_Update_New_Mission";

  const triggerCloudFunction = async (affaire) => {
    try {
      console.log("[CF] Appel avec affaire =", affaire);

      const response = await axios.post(
        PROD_Update_New_Mission_URL,
        { message: affaire },
        {
          headers: { "Content-Type": "application/json" },
          timeout: 30000,
        }
      );

      console.log("[CF] Status:", response.status);
      console.log("[CF] Payload:", response.data);

      if (response.status >= 200 && response.status < 300) {
        const ok =
          typeof response.data === "object" &&
          response.data &&
          response.data.status === "OK";
        return {
          success: ok,
          data: response.data,
          error: ok ? null : "Réponse inattendue de la Cloud Function",
        };
      }

      return {
        success: false,
        data: null,
        error: `HTTP ${response.status}: ${JSON.stringify(response.data)}`,
      };
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      console.error("[CF] Erreur:", status, data || err.message);
      return {
        success: false,
        data: data || null,
        error: status ? `HTTP ${status}` : err.message,
      };
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Prevent duplicate submissions
    if (isSubmitting) {
      return;
    }

    let isValid = true;

    const totalPercentage = domaineDetails.reduce(
      (sum, detail) => sum + Number(detail.percentage),
      0
    );
    if (totalPercentage !== 100) {
      setTotalPercentageError(
        "La somme des pourcentages doit être égale à 100%."
      );
      alert(
        "La somme des pourcentages des CA des domaines doit être égale à 100%."
      );
      isValid = false;
    } else {
      setTotalPercentageError("");
    }

    if (!validateDomaineDetails()) {
      isValid = false;
    } else {
      setSubPercentageError("");
    }

    setFormIsValid(isValid);

    if (isValid) {
      setIsSubmitting(true);
      try {
        const backendUrl =
          "https://us-central1-dataplaform-iac.cloudfunctions.net/readFiles";
        // Extract client and domaine from the selected mission
        const { client, domaine: extractedDomaine } =
          extractClientAndDomaine(selectedAffaire);
        console.log(
          `Extracted from "${selectedAffaire}": Client="${client}", Domaine="${extractedDomaine}"`
        );

        const dataToSubmit = {
          affaire: selectedAffaire,
          facturation: selectedFacturation,
          typeMission: selectedTypeMission,
          client: client,
          domaine: extractedDomaine,
        };

        domaineDetails.forEach((detail) => {
          // Normalize domain name for BigQuery compatibility
          const normalizedDomaine =
            detail.domaine === "C&I" ? "C_I" : detail.domaine;

          if (detail.percentage) {
            dataToSubmit[`percentage_${normalizedDomaine}`] = detail.percentage;
          }
          if (detail.offrePrincipale) {
            dataToSubmit[`offrePrincipale_${normalizedDomaine}`] =
              detail.offrePrincipale;
          }
          if (detail.percentageOffrePrincipale) {
            dataToSubmit[`percentageOffrePrincipale_${normalizedDomaine}`] =
              detail.percentageOffrePrincipale;
          }
          if (detail.offreSecondaire) {
            dataToSubmit[`offreSecondaire_${normalizedDomaine}`] =
              detail.offreSecondaire;
          }
          if (detail.percentageOffreSecondaire) {
            dataToSubmit[`percentageOffreSecondaire_${normalizedDomaine}`] =
              detail.percentageOffreSecondaire;
          }
        });

        // Insert into BigQuery
        const insertResponse = await axios.post(
          backendUrl + "/insertintoBigQuery",
          dataToSubmit
        );

        if (insertResponse.status === 200) {
          // Remove from backend CSV
          axios
            .post(
              backendUrl + "/remove-mission-from-csv",
              { affaire: selectedAffaire },
              { responseType: "text" }
            )
            .then((response) => {
              if (response.status === 200) {
                console.log(
                  "La mise à jour de new-missions.csv a été effectuée"
                );
              } else {
                console.warn(
                  "La mise à jour de new-missions.csv a échouée",
                  response.status
                );
              }
            })
            .catch((error) => {
              console.error(
                "Erreur lors de la suppression de l'affaire sur GCP",
                error
              );
            });

          // Déclenchement de la Cloud Function avec le bon paramètre "message"
          const triggerResult = await triggerCloudFunction(selectedAffaire);

          if (triggerResult.success) {
            console.log("Cloud Function exécutée avec succès :", triggerResult);
          } else {
            console.warn(
              "Cloud Function en anomalie :",
              triggerResult.error,
              triggerResult.data || ""
            );
          }

          setFormIsSent(true);
          removeMissionFromData(selectedAffaire);
          clearAllFields();
          alert("Données insérées avec succès dans BigQuery !");
        } else {
          console.error("Erreur : ", insertResponse.statusText);
          alert(
            "Une erreur est survenue lors de l'insertion des données dans BigQuery."
          );
        }
      } catch (error) {
        console.error("Erreur lors de l'insertion dans BigQuery:", error);
        alert("Une erreur est survenue lors de l'insertion des données.");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        backgroundColor: "#ffffff",
        border: "1px solid #ccc",
        borderRadius: "10px",
        padding: "50px",
      }}
    >
      {formIsSent && (
        <div
          className="formValidatedPopUp"
          onClick={() => setFormIsSent(false)}
        >
          Le formulaire est validé.
        </div>
      )}
      <h1 style={{ color: "#53bccb", textAlign: "center" }}>
        Formulaire nouvelles missions
      </h1>

      {/* Manual Refresh Button */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          marginBottom: "20px",
          gap: "10px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "10px",
            alignItems: "center",
          }}
        >
          <button
            type="button"
            onClick={handleManualRefresh}
            disabled={isRefreshing}
            className="refresh-button"
            title="Cliquez pour synchroniser les données avec le serveur et mettre à jour la liste des missions"
          >
            {isRefreshing ? "🔄 Actualisation..." : "🔄 Actualiser les données"}
          </button>
          {isRefreshing && (
            <div className="refresh-status">Synchronisation en cours...</div>
          )}
        </div>
        <p
          style={{
            fontSize: "12px",
            color: "#666",
            textAlign: "center",
            margin: "0",
            fontStyle: "italic",
          }}
        >
          💡 Utilisez ce bouton si vous pensez que la liste des missions n'est
          pas à jour
        </p>
        {lastRefreshTime && (
          <p
            style={{
              fontSize: "10px",
              color: "#999",
              textAlign: "center",
              margin: "5px 0 0 0",
            }}
          >
            Dernière actualisation : {lastRefreshTime.toLocaleTimeString()}
          </p>
        )}
      </div>

      <p
        style={{
          color: "red",
          fontWeight: "bolder",
        }}
      >
        Attention : Vous êtes responsable des informations que vous saisissez.
        <br />
        Une fois le formulaire soumis, aucune modification ne peut être
        effectuée.
      </p>

      <div>
        <label htmlFor="affaire">
          Affaire :{" "}
          {missionsData.length > 0 &&
            `(${missionsData.length} mission${
              missionsData.length > 1 ? "s" : ""
            } disponible${missionsData.length > 1 ? "s" : ""})`}
        </label>
        <select
          id="affaire"
          value={selectedAffaire}
          onChange={handleAffaireChange}
        >
          <option value="">Sélectionnez une valeur</option>
          {missionsData.map((mission, index) => (
            <option key={index} value={mission.Affaire}>
              {mission.Affaire}
            </option>
          ))}
        </select>
      </div>
      {selectedAffaire && (
        <>
          <div>
            <label htmlFor="facturation">Facturation :</label>
            <select
              id="facturation"
              value={selectedFacturation}
              onChange={handleFacturationChange}
            >
              <option value="" disabled>
                Sélectionnez une valeur
              </option>
              <option value="Forfait">Forfait</option>
              <option value="Regie">Régie</option>
            </select>
          </div>
          <div>
            <label htmlFor="typeMission">Type de mission :</label>
            <select
              id="typeMission"
              value={selectedTypeMission}
              onChange={handleTypeMissionChange}
            >
              <option value="" disabled>
                Sélectionnez une valeur
              </option>
              <option value="interne">Interne</option>
              <option value="externe">Externe</option>
              <option value="mixte">Mixte</option>
            </select>
          </div>
          <div className="checkBox">
            <p>
              <b>Domaine :</b>
            </p>
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  value="DW"
                  checked={selectedDomaines.includes("DW")}
                  onChange={handleDomaineChange}
                />
                DW
              </label>
              <label>
                <input
                  type="checkbox"
                  value="C&I"
                  checked={selectedDomaines.includes("C&I")}
                  onChange={handleDomaineChange}
                />
                C&I
              </label>
              <label>
                <input
                  type="checkbox"
                  value="CS"
                  checked={selectedDomaines.includes("CS")}
                  onChange={handleDomaineChange}
                />
                CS
              </label>
              <label>
                <input
                  type="checkbox"
                  value="EN"
                  checked={selectedDomaines.includes("EN")}
                  onChange={handleDomaineChange}
                />
                EN
              </label>
              <label>
                <input
                  type="checkbox"
                  value="INS"
                  checked={selectedDomaines.includes("INS")}
                  onChange={handleDomaineChange}
                />
                INS
              </label>
            </div>
          </div>
        </>
      )}
      {selectedDomaines.map((domaine) => (
        <div key={domaine}>
          <label
            htmlFor={`percentage_${domaine}`}
          >{`${domaine} : Pourcentage sur le chiffre d'affaire de la mission`}</label>
          <input
            type="number"
            min={0}
            max={100}
            id={`percentage_${domaine}`}
            onChange={(event) =>
              handleDomaineDetailsChange(
                domaine,
                "percentage",
                event.target.value
              )
            }
            style={{
              border: totalPercentageError ? "3px solid red" : "",
            }}
          />
          {totalPercentageError && (
            <p style={{ color: "red", marginTop: "4px", marginBottom: "15px" }}>
              {totalPercentageError}
            </p>
          )}
          <label
            htmlFor={`offrePrincipale_${domaine}`}
          >{`${domaine} : Offre Principale`}</label>
          <select
            id={`offrePrincipale_${domaine}`}
            defaultValue="default"
            onChange={(event) =>
              handleDomaineDetailsChange(
                domaine,
                "offrePrincipale",
                event.target.value
              )
            }
          >
            <option value="default" disabled>
              {offersLoading || offersData.length === 0
                ? "Chargement..."
                : "Sélectionnez une valeur"}
            </option>
            {!offersLoading &&
              offersData.length > 0 &&
              getBlockOptionsForDomaine(domaine).map((Offre) => (
                <option key={Offre} value={Offre}>
                  {Offre}
                </option>
              ))}
          </select>
          {domaineDetails.find(
            (detail) => detail.domaine === domaine && detail.offrePrincipale
          ) && (
            <>
              <label
                htmlFor={`percentageOffrePrincipale_${domaine}`}
              >{`${domaine} : Pourcentage sur l'offre principale`}</label>
              <input
                type="number"
                min={0}
                max={100}
                id={`percentageOffrePrincipale_${domaine}`}
                onChange={(event) =>
                  handleDomaineDetailsChange(
                    domaine,
                    "percentageOffrePrincipale",
                    event.target.value
                  )
                }
                style={{
                  border: subPercentageError ? "3px solid red" : "",
                }}
              />
              {subPercentageError && (
                <p
                  style={{
                    color: "red",
                    marginTop: "4px",
                    marginBottom: "15px",
                  }}
                >
                  {subPercentageError}
                </p>
              )}
              <label
                htmlFor={`offreSecondaire_${domaine}`}
              >{`${domaine} : Offre secondaire`}</label>
              <select
                id={`offreSecondaire_${domaine}`}
                defaultValue="default"
                onChange={(event) =>
                  handleDomaineDetailsChange(
                    domaine,
                    "offreSecondaire",
                    event.target.value
                  )
                }
              >
                <option value="default">
                  {offersLoading || offersData.length === 0
                    ? "Chargement..."
                    : "Sélectionnez une valeur (optionnel)"}
                </option>
                {!offersLoading &&
                  offersData.length > 0 &&
                  getBlockOptionsForDomaine(domaine).map((Offre) => (
                    <option key={Offre} value={Offre}>
                      {Offre}
                    </option>
                  ))}
              </select>
              {domaineDetails.find(
                (detail) => detail.domaine === domaine && detail.offreSecondaire
              ) && (
                <>
                  <label
                    htmlFor={`percentageOffreSecondaire_${domaine}`}
                  >{`${domaine} : Pourcentage sur l'offre secondaire`}</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    id={`percentageOffreSecondaire_${domaine}`}
                    onChange={(event) =>
                      handleDomaineDetailsChange(
                        domaine,
                        "percentageOffreSecondaire",
                        event.target.value
                      )
                    }
                    style={{
                      border: subPercentageError ? "3px solid red" : "",
                    }}
                  />
                  {subPercentageError && (
                    <p
                      style={{
                        color: "red",
                        marginTop: "4px",
                        marginBottom: "15px",
                      }}
                    >
                      {subPercentageError}
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </div>
      ))}
      <button type="submit" disabled={isSubmitting}>
        {isSubmitting ? "Envoi en cours..." : "Valider"}
      </button>
    </form>
  );
};

export default NewMissionForm;